import type { UploadedFont } from "schemas/generated/project";

import { isNotNullish } from "replo-utils/lib/misc";
import { projectHostingTypeSchema } from "schemas/projectHostingType";
import { referralCodeSchema } from "schemas/referralCode";
import { reploSymbolSchema } from "schemas/symbol";
import { userRoleSchema } from "schemas/user";
import { z } from "zod";

export const PROJECT_NAME_MAX_LENGTH = 256;
export const projectNameInputSchema = z
  .string()
  .trim()
  .min(1, "Enter a project name.")
  .max(
    PROJECT_NAME_MAX_LENGTH,
    "Use 256 characters or fewer for your project name.",
  );

export type CurrencyCode = string;

// NOTE (Yuxin, 2026-07-15): Sourced from the platform's ISO 4217 data (Intl), like the timezone picker, instead of a hand-maintained subset.
const FALLBACK_CURRENCIES: Record<string, { name: string }> = {
  USD: { name: "US Dollar" },
  EUR: { name: "Euro" },
  GBP: { name: "British Pound" },
  JPY: { name: "Japanese Yen" },
  CAD: { name: "Canadian Dollar" },
  AUD: { name: "Australian Dollar" },
};

const buildCurrencies = (): Record<string, { name: string }> => {
  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "currency" });
    const entries = Intl.supportedValuesOf("currency").map(
      (code): [string, { name: string }] => [
        code,
        { name: displayNames.of(code) ?? code },
      ],
    );
    return Object.fromEntries(entries);
  } catch {
    // NOTE (Yuxin, 2026-07-15): Fall back on runtimes without Intl currency enumeration so importing this schema never throws.
    return FALLBACK_CURRENCIES;
  }
};

export const CURRENCIES: Record<string, { name: string }> = buildCurrencies();

export const currencySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, "Must be a 3-letter ISO 4217 currency code")
  .describe("Currency");

export const uploadedFontSchema = z
  .object({
    name: z.string(),
    displayName: z.string().optional(),
    type: z.enum(["woff", "woff2"]),
    url: z.string(),
    weight: z.string().optional(),
    style: z.string().optional(),
  })
  .describe("UploadedFont");

export const inUseFontSchema = z
  .object({
    name: z.string(),
    url: z.string(),
    size: z.number(),
    weight: z.number(),
    lineHeight: z.number(),
    letterSpacing: z.number(),
  })
  .describe("InUseFont");

export const inUseFontsSchema = z.array(inUseFontSchema).describe("InUseFonts");

export const shopifyStoreSchema = z
  .object({
    integrationId: z.string().nullish(),
    shopifyIntegrationConfigId: z.string().nullish(),
    shopifyUrl: z.string(),
    url: z.string().nullish(),
    contactEmail: z.string().nullish(),
    shopifyAccessScopes: z.string().nullish(),
    shopifyStorePassword: z.string().nullish(),
  })
  .describe("ShopifyStore");

export const styleLibrarySchema = z
  .object({
    colors: z.object({
      neutrals: z.object({
        light: z.string(),
        subtle: z.string(),
        muted: z.string(),
        dark: z.string(),
      }),
      brand: z.object({
        primary: z.object({
          color: z.string(),
          name: z.string(),
        }),
        secondary: z.object({
          color: z.string(),
          name: z.string(),
        }),
        tertiary: z.object({
          color: z.string(),
          name: z.string(),
        }),
        accent: z.object({
          color: z.string(),
          name: z.string(),
        }),
      }),
    }),
    typography: z.object({
      h1: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      h2: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      h3: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      h4: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
      body: z.object({
        name: z.string(),
        url: z.string(),
        size: z.number(),
        weight: z.number(),
        lineHeight: z.number(),
        letterSpacing: z.number(),
      }),
    }),
    cornerRounding: z.number(),
  })
  .describe("StyleLibrary");

export const reploProjectSchema = z
  .object({
    id: z.uuid(),
    name: z.string().nullish(),
    hostingType: projectHostingTypeSchema,
    shortName: z.string().nullish(),
    createdAt: z.date(),
    referrerKey: z.string().nullish(),
    // NOTE (Fran 2024-06-07): revenue share is calculated manually in the
    // workspaces tab in dashboard
    referrerCode: referralCodeSchema
      .omit({ revenueSharePercentage: true })
      .nullish()
      .describe("ignoreImport:true"),
    tenancy: z.string().nullish(),
    uploadedFonts: z.array(uploadedFontSchema).nullish(),
    ownerId: z.uuid().nullish(),
    ownerWorkspaceId: z.uuid(),
    ownerWorkspace: z.object({
      name: z.string(),
      domain: z.string(),
      id: z.string().nullish(),
    }),
    timeZone: z.string().nullish(),
    currency: currencySchema.nullish(),
    productDescription: z.string().nullish(),
    agentEnabledAt: z.coerce.date().nullish(),
    onboardingWelcomeEnabled: z.boolean().optional(),
    checkoutProvider: z.enum(["shopify", "stripe", "none"]).nullish(),
    sandboxProvider: z.enum(["daytona"]).optional(),
    symbols: z.array(reploSymbolSchema).nullish(),
    script: z.string().nullish(),
    styleLibrary: styleLibrarySchema.nullish(),
    inUseFonts: inUseFontsSchema.nullish(),
    componentsCss: z.string().nullish(),
    urlHeuristics: z.string().nullish(),
    shopMetadataLightModeFaviconUrl: z.string().nullish(),
    shopMetadataDarkModeFaviconUrl: z.string().nullish(),
    logoUrl: z.string().nullish(),
    hasOnboardedStarterSites: z.boolean().nullish(),
    integrations: z
      .object({
        shopify: z
          .object({
            store: shopifyStoreSchema,
          })
          .nullish(),
      })
      .nullish(),
  })
  .describe("ReploProject");

export const reploSimpleProjectSchema = reploProjectSchema
  .pick({
    id: true,
    name: true,
    hostingType: true,
    ownerWorkspaceId: true,
    ownerWorkspace: true,
    ownerId: true,
    createdAt: true,
    integrations: true,
    uploadedFonts: true,
    script: true,
    timeZone: true,
    currency: true,
    logoUrl: true,
    agentEnabledAt: true,
    checkoutProvider: true,
  })
  .extend({
    lastEditedAt: z.date().optional(),
  })
  .describe("ReploSimpleProject");

export const reploProjectWithNoIntegrationsSchema = reploProjectSchema
  .omit({
    integrations: true,
  })
  .describe("ReploProjectWithNoIntegrations");

export const reploSimpleProjectWithStatsSchema = reploSimpleProjectSchema
  .extend({
    elementCounts: z.object({
      pageCount: z.number(),
      productTemplateCount: z.number(),
      sectionCount: z.number(),
      blogCount: z.number(),
    }),
  })
  .describe("ReploSimpleProjectWithStats");

export const reploProjectRefSchema = z
  .object({
    id: z.uuid(),
    hostingType: projectHostingTypeSchema,
  })
  .describe("ReploProjectRef");

// Project deletion issue schemas
export const projectIssueTypeSchema = z
  .enum([
    "publishedElements",
    "shopifyIntegration",
    "experiments",
    "registeredDomains",
  ])
  .describe("ProjectIssueType");

export const projectDeletionIssueSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("publishedElements"),
      count: z.number(),
      message: z.string(),
    }),
    z.object({
      type: z.literal("shopifyIntegration"),
      count: z.number(),
      message: z.string(),
    }),
    z.object({
      type: z.literal("experiments"),
      count: z.number(),
      message: z.string(),
    }),
    z.object({
      type: z.literal("registeredDomains"),
      count: z.number(),
      message: z.string(),
    }),
  ])
  .describe("ProjectDeletionIssue");

export const userWorkspaceSchema = z
  .object({
    workspaceId: z.string(),
    workspaceName: z.string(),
    paymentProcessor: z.string().nullish(),
    currentUserRole: userRoleSchema,
    projects: z.array(reploSimpleProjectSchema),
  })
  .describe("UserWorkspace");

export function isUploadedFont(value: unknown): value is UploadedFont {
  return (
    typeof value === "object" &&
    isNotNullish(value) &&
    "name" in value &&
    typeof value.name === "string" &&
    "type" in value &&
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    ["woff", "woff2"].includes(value.type as string) &&
    "url" in value &&
    typeof value.url === "string"
  );
}
